import React, { useState, useEffect } from 'react'
import { FileText, Edit2, Save, X, ChevronDown, ChevronUp, Plus, Minus, AlertCircle, CheckCircle, Download } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'
import { normalizeStatementData, getProfitLossMetrics, getBalanceSheetMetrics, exportStatementToExcel, exportStatementToCsv } from '../../utils/statementUtils'
import { generateFinancialStatementPDF } from '../../utils/pdfGenerator'
import CumulativeReportPanel from '../../components/CumulativeReportPanel'

const fmt = (n) =>
  new Intl.NumberFormat('en-UG', { style: 'currency', currency: 'UGX', maximumFractionDigits: 0 }).format(n || 0)

// ── inline edit for Profit & Loss data_json ──────────────────────────────────
const ProfitLossEditor = ({ data, onChange }) => {
  const products = data.products || []
  const expenses = data.operating_expenses || []
  const plData = {
    period_start: data.period_start || '',
    period_end: data.period_end || '',
    other_income: data.other_income || '',
    other_expenses: data.other_expenses || '',
    net_income: data.net_income || '',
    notes: data.notes || ''
  }

  const update = (patch) => onChange({ ...data, ...patch })
  const updateProduct = (idx, field, val) => {
    const ps = [...products]
    ps[idx] = { ...ps[idx], [field]: val }
    if (field === 'revenue' || field === 'cost_of_goods_sold') {
      const rev = parseFloat(field === 'revenue' ? val : ps[idx].revenue) || 0
      const cogs = parseFloat(field === 'cost_of_goods_sold' ? val : ps[idx].cost_of_goods_sold) || 0
      ps[idx].gross_profit = (rev - cogs).toString()
    }
    update({ products: ps })
  }
  const addProduct = () => update({ products: [...products, { product_name: '', revenue: '', cost_of_goods_sold: '', gross_profit: '', notes: '' }] })
  const removeProduct = (idx) => update({ products: products.filter((_, i) => i !== idx) })

  const updateExpense = (idx, field, val) => {
    const es = [...expenses]
    es[idx] = { ...es[idx], [field]: val }
    update({ operating_expenses: es })
  }
  const addExpense = () => update({ operating_expenses: [...expenses, { category: '', description: '', amount: '' }] })
  const removeExpense = (idx) => update({ operating_expenses: expenses.filter((_, i) => i !== idx) })

  const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        {[['period_start', 'Period Start'], ['period_end', 'Period End']].map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <input type="date" value={plData[k]} onChange={e => update({ [k]: e.target.value })} className={inp} />
          </div>
        ))}
      </div>

      {/* Products */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-800 text-sm">Products / Income Lines</h4>
          <button onClick={addProduct} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <Plus className="h-3.5 w-3.5" /> Add Product
          </button>
        </div>
        {products.map((p, i) => (
          <div key={i} className="grid grid-cols-5 gap-2 mb-2 p-2 border border-gray-100 rounded-lg bg-gray-50">
            <input value={p.product_name} onChange={e => updateProduct(i, 'product_name', e.target.value)} placeholder="Product name" className={`${inp} col-span-2`} />
            <input type="number" value={p.revenue} onChange={e => updateProduct(i, 'revenue', e.target.value)} placeholder="Revenue" className={inp} />
            <input type="number" value={p.cost_of_goods_sold} onChange={e => updateProduct(i, 'cost_of_goods_sold', e.target.value)} placeholder="COGS" className={inp} />
            <div className="flex items-center gap-1">
              <input type="number" value={p.gross_profit} readOnly placeholder="Gross Profit" className={`${inp} bg-gray-100`} />
              {products.length > 1 && (
                <button onClick={() => removeProduct(i)} className="text-red-400 hover:text-red-600"><Minus className="h-4 w-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Operating Expenses */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-800 text-sm">Operating Expenses</h4>
          <button onClick={addExpense} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
            <Plus className="h-3.5 w-3.5" /> Add Expense
          </button>
        </div>
        {expenses.map((e, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 mb-2 p-2 border border-gray-100 rounded-lg bg-gray-50">
            <input value={e.category} onChange={ev => updateExpense(i, 'category', ev.target.value)} placeholder="Category" className={inp} />
            <input value={e.description} onChange={ev => updateExpense(i, 'description', ev.target.value)} placeholder="Description" className={`${inp} col-span-2`} />
            <div className="flex items-center gap-1">
              <input type="number" value={e.amount} onChange={ev => updateExpense(i, 'amount', ev.target.value)} placeholder="Amount" className={inp} />
              {expenses.length > 1 && (
                <button onClick={() => removeExpense(i)} className="text-red-400 hover:text-red-600"><Minus className="h-4 w-4" /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Summary fields */}
      <div className="grid grid-cols-2 gap-3">
        {[['other_income', 'Other Income'], ['other_expenses', 'Other Expenses'], ['net_income', 'Net Income']].map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <input type="number" value={plData[k]} onChange={e => update({ [k]: e.target.value })} className={inp} />
          </div>
        ))}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
          <textarea value={plData.notes} onChange={e => update({ notes: e.target.value })} rows={2} className={inp} />
        </div>
      </div>
    </div>
  )
}

// ── inline edit for Balance Sheet data_json ──────────────────────────────────
const BalanceSheetEditor = ({ data, onChange }) => {
  const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const section = (title, fields) => (
    <div>
      <h4 className="font-semibold text-gray-800 text-sm mb-2 border-b pb-1">{title}</h4>
      <div className="grid grid-cols-2 gap-3">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
            <input type="number" value={data[k] || ''} onChange={e => onChange({ ...data, [k]: e.target.value })} className={inp} />
          </div>
        ))}
      </div>
    </div>
  )
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">As of Date</label>
        <input type="date" value={data.as_of_date || ''} onChange={e => onChange({ ...data, as_of_date: e.target.value })} className={inp} />
      </div>
      {section('Current Assets', [['cash_and_equivalents','Cash & Equivalents'],['accounts_receivable','Accounts Receivable'],['inventory','Inventory'],['other_current_assets','Other Current Assets'],['total_current_assets','Total Current Assets']])}
      {section('Fixed Assets', [['fixed_assets','Fixed Assets'],['accumulated_depreciation','Accumulated Depreciation'],['net_fixed_assets','Net Fixed Assets'],['other_assets','Other Assets'],['total_assets','Total Assets']])}
      {section('Current Liabilities', [['accounts_payable','Accounts Payable'],['short_term_debt','Short-term Debt'],['other_current_liabilities','Other Current Liabilities'],['total_current_liabilities','Total Current Liabilities']])}
      {section('Long-term Liabilities', [['long_term_debt','Long-term Debt'],['other_liabilities','Other Liabilities'],['total_liabilities','Total Liabilities']])}
      {section('Equity', [['owner_equity','Owner Equity'],['retained_earnings','Retained Earnings'],['total_equity','Total Equity']])}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
        <textarea value={data.notes || ''} onChange={e => onChange({ ...data, notes: e.target.value })} rows={2} className={inp} />
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const MyFinancialStatements = () => {
  const { customer } = useCustomerAuth()
  const [statements, setStatements] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [expandedId, setExpandedId] = useState(null)

  // Edit state
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState(null)
  const [editPeriod, setEditPeriod] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState('')
  const [saveError, setSaveError] = useState('')
  const [downloadingPDF, setDownloadingPDF] = useState(false)
  const [exportingExcel, setExportingExcel] = useState(false)
  const [exportingCsv, setExportingCsv] = useState(false)

  useEffect(() => { fetchStatements() }, [])

  const fetchStatements = async () => {
    setLoading(true)
    setError('')
    try {
      const res = await apiService.getMyFinancialStatements()
      if (res.status === 'SUCCESS') {
        setStatements(res.data || [])
      } else {
        setError(res.message || 'Failed to load statements')
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const startEdit = (stmt) => {
    setEditingId(stmt.id)
    setEditData(JSON.parse(JSON.stringify(stmt.financial_data)))
    setEditPeriod(stmt.reporting_period || '')
    setSaveSuccess('')
    setSaveError('')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditData(null)
    setEditPeriod('')
  }

  const saveEdit = async (stmtId) => {
    setSaving(true)
    setSaveSuccess('')
    setSaveError('')
    try {
      const res = await apiService.updateMyFinancialStatement(stmtId, editData, editPeriod)
      if (res.status === 'SUCCESS') {
        setSaveSuccess('Statement updated successfully!')
        cancelEdit()
        fetchStatements()
      } else {
        setSaveError(res.message || 'Update failed')
      }
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const downloadStatementPDF = async (statement) => {
    setDownloadingPDF(true)
    setSaveError('')
    try {
      const data = normalizeStatementData(statement)
      await generateFinancialStatementPDF({
        customerName: customer?.client_name || customer?.fullName || customer?.name || 'Customer',
        memberId: statement.member_number || statement.customer_id || statement.customerId || 'N/A',
        statementType: statement.statement_type,
        reportingPeriod: statement.reporting_period || data.as_of_date || `${data.period_start || ''} to ${data.period_end || ''}`,
        createdAt: statement.created_at || statement.createdAt,
        financialData: data,
        companyName: statement.company_name || statement.companyName || 'UFCC Microfinance'
      })
    } catch (e) {
      setSaveError('Failed to download PDF: ' + (e.message || 'Unknown error'))
    } finally {
      setDownloadingPDF(false)
    }
  }

  const exportStatementAsExcel = async (statement) => {
    setExportingExcel(true)
    setSaveError('')
    try {
      const filename = `statement_${statement.id || 'unknown'}.xlsx`
      exportStatementToExcel(statement, filename)
    } catch (e) {
      setSaveError('Failed to export XLSX: ' + (e.message || 'Unknown error'))
    } finally {
      setExportingExcel(false)
    }
  }

  const exportStatementAsCsv = async (statement) => {
    setExportingCsv(true)
    setSaveError('')
    try {
      const filename = `statement_${statement.id || 'unknown'}.csv`
      exportStatementToCsv(statement, filename)
    } catch (e) {
      setSaveError('Failed to export CSV: ' + (e.message || 'Unknown error'))
    } finally {
      setExportingCsv(false)
    }
  }

  const statementLabel = (type) =>
    type === 'profit_loss' ? 'Profit & Loss' : type === 'balance_sheet' ? 'Balance Sheet' : type

  const statementBadge = (type) =>
    type === 'profit_loss'
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-blue-100 text-blue-700 border-blue-200'

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-8 w-8 text-blue-600" />
            My Financial Statements
          </h1>
          <p className="text-gray-600 mt-1">
            View and edit the financial statements you have submitted.
          </p>
        </div>

        <div className="mb-6">
          <CumulativeReportPanel
            customerName={customer?.client_name || customer?.fullName || customer?.name || ''}
          />
        </div>

        {saveSuccess && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 mb-4">
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
            {saveSuccess}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
          </div>
        )}

        {!loading && statements.length === 0 && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
            <p className="text-lg font-medium">No financial statements yet</p>
            <p className="text-sm mt-1">Go to <a href="/customer/financial-data" className="text-blue-600 underline">Financial Data Entry</a> to submit your first statement.</p>
          </div>
        )}

        <div className="space-y-4">
          {statements.map((stmt) => (
            <div key={stmt.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header row */}
              <div
                className="flex flex-wrap items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedId(expandedId === stmt.id ? null : stmt.id)}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statementBadge(stmt.statement_type)}`}>
                    {statementLabel(stmt.statement_type)}
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900">{stmt.reporting_period || 'No Period'}</p>
                    <p className="text-xs text-gray-400">
                      Submitted: {new Date(stmt.created_at).toLocaleDateString()}
                      {stmt.updated_at !== stmt.created_at && ` · Updated: ${new Date(stmt.updated_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {editingId !== stmt.id && (
                    <button
                      onClick={e => { e.stopPropagation(); startEdit(stmt); setExpandedId(stmt.id) }}
                      className="flex items-center gap-1 text-sm text-blue-600 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50"
                    >
                      <Edit2 className="h-3.5 w-3.5" /> Edit
                    </button>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); downloadStatementPDF(stmt) }}
                    disabled={downloadingPDF}
                    className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <Download className="h-3.5 w-3.5" /> PDF
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); exportStatementAsExcel(stmt) }}
                    disabled={exportingExcel}
                    className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    XLSX
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); exportStatementAsCsv(stmt) }}
                    disabled={exportingCsv}
                    className="flex items-center gap-1 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    CSV
                  </button>
                  {expandedId === stmt.id ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
              </div>

              {/* Expanded view / editor */}
              {expandedId === stmt.id && (
                <div className="border-t border-gray-100 p-4">
                  {editingId === stmt.id ? (
                    <div>
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Reporting Period</label>
                        <input
                          type="text"
                          value={editPeriod}
                          onChange={e => setEditPeriod(e.target.value)}
                          placeholder="e.g. 2024-Q1"
                          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {stmt.statement_type === 'profit_loss' && (
                        <ProfitLossEditor data={editData} onChange={setEditData} />
                      )}
                      {stmt.statement_type === 'balance_sheet' && (
                        <BalanceSheetEditor data={editData} onChange={setEditData} />
                      )}

                      {saveError && (
                        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mt-3 text-sm">
                          <AlertCircle className="h-4 w-4" /> {saveError}
                        </div>
                      )}

                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => saveEdit(stmt.id)}
                          disabled={saving}
                          className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
                        >
                          <Save className="h-4 w-4" />
                          {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center gap-2 text-gray-600 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 text-sm"
                        >
                          <X className="h-4 w-4" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Read-only summary
                    <div>
                      {stmt.statement_type === 'profit_loss' && (
                        <div>
                          {(() => {
                            const data = normalizeStatementData(stmt)
                            const metrics = getProfitLossMetrics(data)
                            return (
                              <>
                                {metrics.products.length > 0 && (
                                  <div className="mb-4">
                                    <h4 className="font-semibold text-gray-800 text-sm mb-2">Products</h4>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
                                        <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                                          <tr>
                                            <th className="px-3 py-2 text-left">Product</th>
                                            <th className="px-3 py-2 text-right">Revenue</th>
                                            <th className="px-3 py-2 text-right">COGS</th>
                                            <th className="px-3 py-2 text-right">Gross Profit</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {metrics.products.map((p, i) => (
                                            <tr key={i} className="border-t border-gray-100">
                                              <td className="px-3 py-2 font-medium">{p.product_name}</td>
                                              <td className="px-3 py-2 text-right text-green-700">{fmt(p.revenue)}</td>
                                              <td className="px-3 py-2 text-right text-red-600">{fmt(p.cost_of_goods_sold)}</td>
                                              <td className="px-3 py-2 text-right text-blue-700">{fmt(p.gross_profit)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div className="bg-gray-50 rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-2">Total Revenue</div>
                                    <div className="text-lg font-semibold text-gray-900">{fmt(metrics.totalRevenue)}</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-2">Total COGS</div>
                                    <div className="text-lg font-semibold text-red-600">{fmt(metrics.totalCOGS)}</div>
                                  </div>
                                </div>

                                <div className="bg-emerald-50 p-4 rounded-xl mb-4">
                                  <div className="text-xs text-gray-500">Gross Profit</div>
                                  <div className="text-xl font-semibold text-emerald-700">{fmt(metrics.totalGrossProfit)}</div>
                                </div>

                                {metrics.expenses.length > 0 && (
                                  <div className="mb-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="font-semibold text-gray-800 text-sm">Operating Expenses</h4>
                                      <div className="text-sm font-medium text-red-600">{fmt(metrics.totalOperatingExpenses)}</div>
                                    </div>
                                    <div className="space-y-2">
                                      {metrics.expenses.map((e, i) => (
                                        <div key={i} className="flex justify-between text-sm text-gray-700">
                                          <span>{e.category || e.expense_category}</span>
                                          <span>{fmt(e.amount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                  <div className="bg-gray-50 rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-2">Other Income</div>
                                    <div className="text-lg font-semibold text-emerald-700">{fmt(metrics.otherIncome)}</div>
                                  </div>
                                  <div className="bg-gray-50 rounded-xl p-4">
                                    <div className="text-xs text-gray-500 mb-2">Other Expenses</div>
                                    <div className="text-lg font-semibold text-red-600">{fmt(metrics.otherExpenses)}</div>
                                  </div>
                                </div>

                                <div className={`py-3 px-4 rounded-xl font-semibold text-base ${metrics.netIncome >= 0 ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
                                  <div className="flex justify-between">
                                    <span>Net Profit</span>
                                    <span>{fmt(metrics.netIncome)}</span>
                                  </div>
                                </div>

                                {data.notes && <p className="mt-4 text-xs text-gray-500 italic border-t pt-3">Notes: {data.notes}</p>}
                              </>
                            )
                          })()}
                        </div>
                      )}
                      {stmt.statement_type === 'balance_sheet' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          {[['total_assets','Total Assets','green'],['total_liabilities','Total Liabilities','red'],['total_equity','Total Equity','blue']].map(([k, label, color]) => (
                            stmt.financial_data?.[k] ? (
                              <div key={k} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-3`}>
                                <p className="text-xs text-gray-500">{label}</p>
                                <p className={`font-bold text-${color}-700`}>{fmt(stmt.financial_data[k])}</p>
                              </div>
                            ) : null
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MyFinancialStatements
